import { Icon, Text } from '@zillow/constellation-10';
import type { ReactNode } from 'react';
import { getPastDueDays, type PaymentHistoryItemCommon } from '@zg-rentals/applications-utils';
import { IconCheckmarkFilled, IconCloseFilled, IconHideFilled } from '@zillow/constellation-icons';
import { type PaymentGridCell, PaymentHistoryGridCommon, PaymentNoDataCell } from './PaymentHistoryGridCommon';

type PaymentColor = 'green.600' | 'yellow.500' | 'red.600' | 'black';

type J9CellStyle =
  | { display: 'icon'; icon: ReactNode; color: PaymentColor }
  | { display: 'no-data' }
  | { display: 'code'; color: PaymentColor };

const ON_TIME: J9CellStyle = { display: 'icon', icon: <IconCheckmarkFilled />, color: 'green.600' };
const CHARGE_OFF: J9CellStyle = { display: 'icon', icon: <IconCloseFilled />, color: 'red.600' };
const CLOSED: J9CellStyle = { display: 'icon', icon: <IconHideFilled />, color: 'black' };
const NO_DATA: J9CellStyle = { display: 'no-data' };
const DEROGATORY: J9CellStyle = { display: 'code', color: 'red.600' };

// Icon/color mappings
export const J9_CELL_STYLES: Record<string, J9CellStyle> = {
  A10: ON_TIME,
  A1C: ON_TIME,

  '878': DEROGATORY,
  '898': DEROGATORY,
  '889': DEROGATORY,
  '919': DEROGATORY,
  '929': DEROGATORY,
  '93G': DEROGATORY,
  '94H': DEROGATORY,
  '95J': DEROGATORY,
  '96K': DEROGATORY,

  '689': CLOSED,
  A3B: CLOSED,
  '03B': CLOSED,
  DAB: CLOSED,
  A2B: CLOSED,
  '10B': CLOSED,
  UB: CLOSED,
  '05B': CLOSED,

  '97L': CHARGE_OFF,

  H7: NO_DATA,
  D7: NO_DATA,
  Y7: NO_DATA,
  '1A7': NO_DATA,
  F9: NO_DATA,
  B9: NO_DATA,
  W9: NO_DATA,
  G9: NO_DATA,
  C9: NO_DATA,
  X9: NO_DATA,
  E9: NO_DATA,
  V9: NO_DATA,
  '679': NO_DATA,
  Z9: NO_DATA,
  A9: NO_DATA,
  'N/A': NO_DATA,
  JB: NO_DATA,
  NB: NO_DATA,
  KB: NO_DATA,
  OB: NO_DATA,
  PB: NO_DATA,
  IB: NO_DATA,
  MB: NO_DATA,
  RB: NO_DATA,
  DFB: NO_DATA,
  TB: NO_DATA,
};

export function PaymentCellIconJ9({ code, desc }: { code: string; desc: string }) {
  const pastDueDays = getPastDueDays(code);
  if (pastDueDays) {
    return (
      <Text textStyle="body-sm-bold" title={desc} css={{ color: 'yellow.500' }}>
        {pastDueDays}
      </Text>
    );
  }

  const style = J9_CELL_STYLES[code.trim()];

  const title = [code, desc].filter(Boolean).join(' - ');
  if (code.trim() === '' || style?.display === 'no-data') {
    return <PaymentNoDataCell title={title} />;
  }
  if (style?.display === 'icon') {
    return (
      <Icon role="img" title={title} css={{ color: style.color }}>
        {style.icon}
      </Icon>
    );
  }
  return (
    <Text textStyle="body-sm-bold" title={desc} css={style?.display === 'code' ? { color: style.color } : undefined}>
      {code}
    </Text>
  );
}

export function PaymentHistoryGridJ9({ items }: { items: Array<PaymentHistoryItemCommon> }) {
  const cells: Array<PaymentGridCell> = items.map(({ year, month, code, desc }) => ({
    year,
    month,
    code,
    cell: <PaymentCellIconJ9 code={code} desc={desc ?? ''} />,
    legend: desc ?? '',
  }));

  return <PaymentHistoryGridCommon cells={cells} />;
}
